import { useQuery } from '@tanstack/react-query'
import { useRouterState } from '@tanstack/react-router'
import { useCallback, useMemo, useState, type JSX, type ReactNode } from 'react'

import { PageAlertContext, usePageAlert } from '../hooks/use-page-alert.ts'
import { Button } from '../design-system/button.tsx'
import {
  ALERT_REPORTS_QUERY_KEY,
  fetchAlertReports,
  type AlertReport
} from '../lib/alert-rules-api.ts'
import { QUIET_QUERY_META } from '../lib/query-activity.ts'
import { AlertSheet } from './alert-sheet.tsx'

export { usePageAlert } from '../hooks/use-page-alert.ts'

const NO_REPORTS: readonly AlertReport[] = []

/**
 * The alert catalogue and the one sheet for the page on screen.
 *
 * The button and page search both open this sheet. The open state lives
 * here, above both of them, so search does not grow a second sheet. A path
 * change closes it before paint, which is what a fresh page would do.
 *
 * The catalogue changes only with a deploy, so it loads once per session.
 *
 * @param props - Provider props.
 * @param props.children - The console.
 * @param props.enabled - Whether the console needs the catalogue. Off for display mode.
 * @returns The provider.
 */
export function PageAlertProvider({
  children,
  enabled = true
}: {
  children: ReactNode
  enabled?: boolean
}): JSX.Element {
  const pathname = useRouterState({ select: state => state.location.pathname })
  const [open, setOpen] = useState(false)
  const [openPath, setOpenPath] = useState(pathname)
  const [pending, setPending] = useState<{
    path: string
    from: string
    arrived: boolean
  } | null>(null)
  if (openPath !== pathname) {
    setOpenPath(pathname)
    setOpen(false)
  }
  const catalogue = useQuery({
    queryKey: ALERT_REPORTS_QUERY_KEY,
    meta: QUIET_QUERY_META,
    queryFn: fetchAlertReports,
    enabled,
    staleTime: Infinity,
    retry: false
  })
  const reports = enabled ? (catalogue.data?.reports ?? NO_REPORTS) : NO_REPORTS
  const catalogueLoaded = enabled && catalogue.isSuccess
  const report = reports.find(item => item.path === pathname) ?? null
  if (pending !== null) {
    // Once the reader has reached the report, leaving it cancels the request,
    // even for the page the request came from.
    const leftForAnotherPath =
      pathname !== pending.path &&
      (pending.arrived || pathname !== pending.from)
    const missingReport =
      catalogue.isSuccess && !reports.some(item => item.path === pending.path)
    if (!enabled || leftForAnotherPath || missingReport || catalogue.isError) {
      setPending(null)
    } else if (pathname === pending.path && report !== null) {
      setOpen(true)
      setPending(null)
    } else if (pathname === pending.path && !pending.arrived) {
      setPending({ ...pending, arrived: true })
    }
  }
  const openAlertOn = useCallback(
    (path: string) => {
      setPending({ path, from: pathname, arrived: false })
    },
    [pathname]
  )
  const openAlert = useCallback(() => setOpen(true), [])
  const value = useMemo(
    () => ({
      reports,
      catalogueLoaded,
      report,
      open,
      setOpen,
      openAlert,
      openAlertOn
    }),
    [reports, catalogueLoaded, report, open, openAlert, openAlertOn]
  )

  return <PageAlertContext value={value}>{children}</PageAlertContext>
}

/**
 * AlertMe — the Alert me button at the top of every alertable report page.
 *
 * The root layout renders it above every page. It finds the page by matching
 * the path against the paths `GET /alert-reports` lists, so no page declares
 * anything: a report the backend declares is alertable once it exists. On a
 * path the catalogue does not list, and while the catalogue loads or fails,
 * it renders nothing.
 *
 * @returns The button and its sheet, or nothing.
 */
export function AlertMe(): JSX.Element | null {
  const { report, open, setOpen, openAlert } = usePageAlert()
  if (!report) return null

  return (
    <div data-testid="alert-me-bar" className="flex justify-end">
      <Button data-testid="alert-me" variant="outline" onClick={openAlert}>
        Alert me
      </Button>
      <AlertSheet
        key={report.path}
        report={report}
        open={open}
        onOpenChange={setOpen}
      />
    </div>
  )
}
